import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Edit2, Copy, Trash2, ChevronDown, ChevronUp, Loader2, RefreshCw, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { useMyTrucks, type MyTruckRow, type TruckStatus } from "@/hooks/useMyTrucks";
import { TruckDetailsPanel } from "./TruckDetailsPanel";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const FILTER_TABS = ["All", "Active", "Expired", "Private"] as const;
const STATE_CHIPS = ["TX", "NY", "CA", "FL", "IL"];

type MyTruckSortOption =
  | "ref_az" | "ref_za"
  | "status_az" | "status_za"
  | "origin_az" | "origin_za"
  | "dest_az" | "dest_za"
  | "equip_az" | "equip_za"
  | "length_asc" | "length_desc"
  | "weight_asc" | "weight_desc"
  | "type_az" | "type_za"
  | "rpm_asc" | "rpm_desc"
  | "mkt_asc" | "mkt_desc"
  | "avail_newest" | "avail_oldest"
  | "age_newest" | "age_oldest";

type SortHeaderKey =
  | "ref" | "status" | "origin" | "dest" | "equip"
  | "length" | "weight" | "type" | "rpm" | "mkt" | "avail" | "age";

const HEADER_SORT_MAP: Record<SortHeaderKey, [MyTruckSortOption, MyTruckSortOption]> = {
  ref:    ["ref_az", "ref_za"],
  status: ["status_az", "status_za"],
  origin: ["origin_az", "origin_za"],
  dest:   ["dest_az", "dest_za"],
  equip:  ["equip_az", "equip_za"],
  length: ["length_desc", "length_asc"],
  weight: ["weight_desc", "weight_asc"],
  type:   ["type_az", "type_za"],
  rpm:    ["rpm_desc", "rpm_asc"],
  mkt:    ["mkt_desc", "mkt_asc"],
  avail:  ["avail_newest", "avail_oldest"],
  age:    ["age_newest", "age_oldest"],
};

function SortableTH({ label, headerKey, sortBy, onSort }: {
  label: string;
  headerKey: SortHeaderKey;
  sortBy: MyTruckSortOption;
  onSort: (s: MyTruckSortOption) => void;
}) {
  const [primary, reverse] = HEADER_SORT_MAP[headerKey];
  const isActive = sortBy === primary || sortBy === reverse;
  const isReversed = sortBy === reverse;

  const handleClick = () => {
    if (!isActive) onSort(primary);
    else onSort(isReversed ? primary : reverse);
  };

  return (
    <th
      onClick={handleClick}
      className={cn(
        "px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none transition-colors hover:text-foreground",
        isActive ? "text-foreground" : "text-muted-foreground"
      )}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {isActive && (isReversed
          ? <ChevronUp className="h-2.5 w-2.5" />
          : <ChevronDown className="h-2.5 w-2.5" />
        )}
      </span>
    </th>
  );
}

function sortTrucks(trucks: MyTruckRow[], sortBy: MyTruckSortOption): MyTruckRow[] {
  const arr = [...trucks];
  const cmp = (a: number, b: number) => a - b;
  const strCmp = (a: string, b: string) => a.localeCompare(b);
  switch (sortBy) {
    case "ref_az":       return arr.sort((a, b) => strCmp(a.ref, b.ref));
    case "ref_za":       return arr.sort((a, b) => strCmp(b.ref, a.ref));
    case "status_az":    return arr.sort((a, b) => strCmp(a.status, b.status));
    case "status_za":    return arr.sort((a, b) => strCmp(b.status, a.status));
    case "origin_az":    return arr.sort((a, b) => strCmp(a.origin, b.origin));
    case "origin_za":    return arr.sort((a, b) => strCmp(b.origin, a.origin));
    case "dest_az":      return arr.sort((a, b) => strCmp(a.destination, b.destination));
    case "dest_za":      return arr.sort((a, b) => strCmp(b.destination, a.destination));
    case "equip_az":     return arr.sort((a, b) => strCmp(a.equipmentType, b.equipmentType));
    case "equip_za":     return arr.sort((a, b) => strCmp(b.equipmentType, a.equipmentType));
    case "length_asc":   return arr.sort((a, b) => cmp(a.lengthFt, b.lengthFt));
    case "length_desc":  return arr.sort((a, b) => cmp(b.lengthFt, a.lengthFt));
    case "weight_asc":   return arr.sort((a, b) => cmp(a.weightLbs, b.weightLbs));
    case "weight_desc":  return arr.sort((a, b) => cmp(b.weightLbs, a.weightLbs));
    case "type_az":      return arr.sort((a, b) => strCmp(a.loadType, b.loadType));
    case "type_za":      return arr.sort((a, b) => strCmp(b.loadType, a.loadType));
    case "rpm_asc":      return arr.sort((a, b) => cmp(a.ratePerMile ?? 0, b.ratePerMile ?? 0));
    case "rpm_desc":     return arr.sort((a, b) => cmp(b.ratePerMile ?? 0, a.ratePerMile ?? 0));
    case "mkt_asc":      return arr.sort((a, b) => cmp(a.marketRpm ?? 0, b.marketRpm ?? 0));
    case "mkt_desc":     return arr.sort((a, b) => cmp(b.marketRpm ?? 0, a.marketRpm ?? 0));
    case "avail_newest": return arr.sort((a, b) => new Date(b.availableFrom).getTime() - new Date(a.availableFrom).getTime());
    case "avail_oldest": return arr.sort((a, b) => new Date(a.availableFrom).getTime() - new Date(b.availableFrom).getTime());
    case "age_newest":   return arr.sort((a, b) => cmp(a.ageHours, b.ageHours));
    case "age_oldest":   return arr.sort((a, b) => cmp(b.ageHours, a.ageHours));
    default:             return arr;
  }
}

const STATUS_STYLES: Record<TruckStatus, { label: string; className: string }> = {
  active:  { label: "Active",  className: "bg-primary/10 text-primary border-primary/20" },
  private: { label: "Private", className: "bg-info-subtle text-info border-info/20" },
  expired: { label: "Expired", className: "bg-muted text-muted-foreground border-border" },
  booked:  { label: "Booked",  className: "bg-warning-subtle text-warning border-warning/20" },
};

function ageLabel(h: number) {
  if (h < 1) return "<1h";
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** Try to extract a US state code from an "City, ST" or "City, ST 12345" origin string. */
function extractState(loc: string): string {
  const m = loc.match(/,\s*([A-Z]{2})(?:\b|$)/);
  return m ? m[1] : "";
}

const REMOVE_REASONS = [
  { value: 'booked',    label: 'Booked',    description: 'Truck got a load — remove from board' },
  { value: 'cancelled', label: 'Cancelled', description: 'Driver no longer available' },
  { value: 'private',   label: 'Unposted',  description: 'Temporarily hide from board' },
  { value: 'deleted',   label: 'Deleted',   description: 'Permanently remove this posting' },
] as const;

export function MyTrucksTableV2() {
  const { trucks, loading, refetch } = useMyTrucks();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [activeStates, setActiveStates] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<MyTruckSortOption>("age_newest");
  const [expandedTruckId, setExpandedTruckId] = useState<string | null>(null);

  // Derive available state chips from posted truck origins
  const availableChips = useMemo(() => {
    const set = new Set(trucks.map(t => extractState(t.origin)).filter(Boolean));
    return STATE_CHIPS.filter(s => set.has(s));
  }, [trucks]);

  const filtered = useMemo(() => {
    let result = trucks;
    if (activeFilter !== "All") {
      result = result.filter(t => t.status === activeFilter.toLowerCase());
    }
    if (activeStates.length > 0) {
      result = result.filter(t => activeStates.includes(extractState(t.origin)));
    }
    return sortTrucks(result, sortBy);
  }, [trucks, activeFilter, activeStates, sortBy]);

  const handleEdit = (truckId: string) => navigate(`/post-truck?edit=${truckId}`);
  const handleDuplicate = (truckId: string) => navigate(`/post-truck?duplicate=${truckId}`);
  const handleRepost = (truckId: string) => navigate(`/post-truck?repost=${truckId}`);

  const handleRemoveWithReason = async (truckId: string, reason: typeof REMOVE_REASONS[number]['value']) => {
    if (!user) return;
    try {
      if (reason === 'deleted') {
        // Hard delete per product decision
        const { error } = await supabase
          .from('truck_postings')
          .delete()
          .eq('id', truckId)
          .eq('created_by', user.id);
        if (error) throw error;
        toast({ title: "Truck deleted", description: "The posting has been permanently removed." });
      } else {
        const { error } = await supabase
          .from('truck_postings')
          .update({ status: reason })
          .eq('id', truckId)
          .eq('created_by', user.id);
        if (error) throw error;
        const label = REMOVE_REASONS.find(r => r.value === reason)?.label ?? reason;
        toast({
          title: "Truck removed",
          description: reason === 'private'
            ? "Truck unposted — hidden from the board."
            : `The truck has been marked as ${label} and removed from the board.`,
        });
      }
      refetch();
    } catch (err: any) {
      toast({ title: "Failed to update truck", description: err.message, variant: "destructive" });
    }
  };

  return (
    <div className="data-table flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      {/* Filter tab bar */}
      <div className="flex items-center gap-0 border-b bg-muted/40 px-3 pt-1.5">
        {FILTER_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveFilter(tab)}
            className={cn(
              "relative px-3 py-1.5 text-[11px] font-semibold transition-colors border-b-2 -mb-px",
              activeFilter === tab
                ? "border-accent text-accent"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {tab}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 pb-2">
          {availableChips.map(s => (
            <button
              key={s}
              onClick={() => setActiveStates(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors",
                activeStates.includes(s)
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border text-muted-foreground hover:border-accent hover:text-foreground"
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[11px]">Loading trucks…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <span className="text-[11px]">No trucks found. Post a truck to get started.</span>
          </div>
        ) : (
          <table className="w-full text-caption">
            <thead>
              <tr className="border-b bg-muted/40">
                <SortableTH label="Ref #"       headerKey="ref"    sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Status"      headerKey="status" sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Origin"      headerKey="origin" sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Destination" headerKey="dest"   sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Equip."      headerKey="equip"  sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Length"      headerKey="length" sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Weight"      headerKey="weight" sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Type"        headerKey="type"   sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Rate/Mi"    headerKey="rpm"    sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Mkt RPM"     headerKey="mkt"    sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Available"   headerKey="avail"  sortBy={sortBy} onSort={setSortBy} />
                <SortableTH label="Age"         headerKey="age"    sortBy={sortBy} onSort={setSortBy} />
                <th className="px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wide text-muted-foreground whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => {
                const isExpanded = expandedTruckId === row.id;
                const statusStyle = STATUS_STYLES[row.status];
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpandedTruckId(prev => prev === row.id ? null : row.id)}
                      className={cn(
                        "border-b transition-colors hover:bg-muted/40 cursor-pointer",
                        i % 2 === 0 ? "bg-card" : "bg-muted/10",
                        isExpanded && "border-l-2 border-l-accent bg-muted/30"
                      )}
                    >
                      <td className="px-3 py-1.5 font-mono font-semibold text-foreground whitespace-nowrap text-[11px]">{row.ref}</td>
                      <td className="px-3 py-1.5">
                        <span className={cn("rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide", statusStyle.className)}>
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-foreground whitespace-nowrap text-[11px]">{row.origin}</td>
                      <td className="px-3 py-1.5 text-foreground whitespace-nowrap text-[11px]">{row.destination}</td>
                      <td className="px-3 py-1.5">
                        <span className={cn(
                          "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                          row.equipmentType === "Van" && "bg-info-subtle text-info",
                          row.equipmentType === "Reefer" && "bg-ai-subtle text-ai",
                          row.equipmentType === "Flatbed" && "bg-warning-subtle text-warning",
                          !["Van", "Reefer", "Flatbed"].includes(row.equipmentType) && "bg-muted text-muted-foreground"
                        )}>
                          {row.equipmentType}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-muted-foreground text-[11px]">{row.lengthFt > 0 ? `${row.lengthFt} ft` : '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-muted-foreground text-[11px]">{row.weightLbs > 0 ? row.weightLbs.toLocaleString() : '—'}</td>
                      <td className="px-3 py-1.5 text-muted-foreground text-[11px]">{row.loadType}</td>
                      <td className="px-3 py-1.5 font-mono font-semibold text-foreground text-[11px]">
                        {row.ratePerMile != null && row.ratePerMile > 0 ? `$${Number(row.ratePerMile).toFixed(2)}/mi` : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        {row.marketRpm != null && row.marketRpm > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {row.rateVsMarket === "up" && (
                              <span
                                className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold w-fit"
                                style={{ backgroundColor: "hsl(var(--brand-teal-deep))", color: "#ffffff" }}
                                title="Your ask is above current market RPM"
                              >
                                <ArrowUpRight className="h-3 w-3" />${row.marketRpm.toFixed(2)}
                              </span>
                            )}
                            {row.rateVsMarket === "down" && (
                              <span
                                className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold w-fit"
                                style={{ backgroundColor: "hsl(var(--danger) / 0.12)", color: "hsl(var(--danger))" }}
                                title="Your ask is below current market RPM"
                              >
                                <ArrowDownRight className="h-3 w-3" />${row.marketRpm.toFixed(2)}
                              </span>
                            )}
                            {row.rateVsMarket === "neutral" && (
                              <span
                                className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold w-fit"
                                style={{ backgroundColor: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))" }}
                                title="Your ask matches current market RPM"
                              >
                                <Minus className="h-3 w-3" />${row.marketRpm.toFixed(2)}
                              </span>
                            )}
                            {row.rateVsMarket === null && (
                              <span className="font-mono text-muted-foreground text-[11px]">${row.marketRpm.toFixed(2)}</span>
                            )}
                            {row.rateVsMarketPercent !== null && (
                              <span className={cn(
                                "text-[9px] font-medium pl-1",
                                row.rateVsMarket === "up" && "text-primary",
                                row.rateVsMarket === "down" && "text-destructive",
                                row.rateVsMarket === "neutral" && "text-muted-foreground",
                              )}>
                                {row.rateVsMarketPercent > 0 ? `+${row.rateVsMarketPercent}%` : `${row.rateVsMarketPercent}%`} vs mkt
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-[11px]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground text-[11px] whitespace-nowrap">
                        {format(new Date(row.availableFrom), 'MMM dd')}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap text-[11px]">{ageLabel(row.ageHours)}</td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
                            className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-2.5 w-2.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRepost(row.id); }}
                            className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            title="Repost"
                          >
                            <RefreshCw className="h-2.5 w-2.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDuplicate(row.id); }}
                            className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            title="Duplicate Truck"
                          >
                            <Copy className="h-2.5 w-2.5" />
                          </button>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                onClick={(e) => { e.stopPropagation(); }}
                                className="flex h-5 w-5 items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                title="Remove Truck"
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-52 p-1" onClick={(e) => e.stopPropagation()}>
                              <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Remove reason</p>
                              {REMOVE_REASONS.map(r => (
                                <button
                                  key={r.value}
                                  onClick={() => { handleRemoveWithReason(row.id, r.value); }}
                                  className="flex w-full flex-col items-start rounded px-2 py-1.5 text-left hover:bg-muted transition-colors"
                                >
                                  <span className="text-sm font-medium text-foreground">{r.label}</span>
                                  <span className="text-[10px] text-muted-foreground">{r.description}</span>
                                </button>
                              ))}
                            </PopoverContent>
                          </Popover>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${row.id}-detail`} className="border-b">
                        <td colSpan={13} className="p-0">
                          <TruckDetailsPanel truck={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center border-t bg-muted/20 px-3 py-2">
        <div className="flex items-center gap-1.5 text-caption text-muted-foreground">
          <span className="text-[10px]">◎</span>
          <span>{filtered.length} trucks</span>
          <span>·</span>
          <span className="text-[10px]">Powered by ATLAS</span>
        </div>
      </div>
    </div>
  );
}
